import type { ResultStatusCode } from './result-status.models';

export interface BreedCategory {
  titleKey: string;
  breeds: string[];
  rawBreeds?: DogBreed[];
}

export interface GalleryImage {
  url: string;
  title?: string;
  description?: string;
}

export interface Club {
  id: string;
  name: string;
  region: string;
  departmentCode: string; // Changed from department_code to match camelCase
  logoUrl?: string;
}

export interface Human {
  id: string;
  name: string;
  age: number;
  gender: 'Male' | 'Female' | 'Other';
  club?: string; // flattened for display
  nationality?: 'FRA' | 'BEL' | 'ENG';
  imageUrl?: string;
  coverImageUrl?: string;
}

export interface Dog {
  id: string;
  ownerId: string;
  name: string;
  age: number;
  birthYear?: number | null;
  breed: string;
  gender: 'Male' | 'Female';
  imageUrl?: string;
}

// In DB, team has human_id and dog_id.
// In frontend, we often need the full object.
export interface Team {
  id: string;
  humanId: string;
  dogId: string;
  teamName: string;
  human: Human;
  dog: Dog;
}

export interface Event {
  id: string;
  name: string;
  location: string;
  date: string; // ISO string format
  weather?: string;
  description?: string;
  imageUrl?: string;
  region?: string;
  organizingClub?: string;
  city?: string;
  postalCode?: string;
  departmentCode?: string;
  inseeCode?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  geoPrecision?: 'venue' | 'address' | 'postal_code' | 'city_center';
  geoSource?: string;
  geoConfidence?: 'high' | 'medium' | 'low';
  status?: 'upcoming' | 'completed';
  isPublished?: boolean;
  races?: Race[];
  eventPhotos?: GalleryImage[];
}

export function formatEventLocation(event: Pick<Event, 'city' | 'departmentCode' | 'location'>): string {
  if (event.city && event.departmentCode) return `${event.city} (${event.departmentCode})`;
  return event.city || event.location || '';
}

export function normalizeLocation(value: unknown): string {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';

  const location = value as Record<string, unknown>;
  const parts = ['name', 'city', 'commune', 'label', 'country']
    .map(key => location[key])
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0);

  return parts.join(', ');
}

export interface Race {
  id: string;
  eventId: string;
  name: string;
  distanceKm: number;
  terrain: 'Trail' | 'Road' | 'Mixed';
  discipline?: 'Canicross' | 'CanicrossCourt' | 'Cani-VTT' | 'Cani-Trottinette' | 'Cani-Marche' | 'Cani-Trail' | 'CanicrossLong' | 'CanicrossCadet' | 'CanicrossMinime' | 'CanicrossBenjamin';
}

export interface Result {
  id: string;
  raceId: string; // foreign key to Race table
  eventId?: string; // Optional helper (frontend often needs event context)
  teamId: string; // foreign key
  timeSeconds: number;
  position?: number;
  category?: string;
  bibNumber?: string | number;
  discipline?: 'Cani-VTT' | 'Cani-Trottinette' | 'Canicross' | 'CanicrossCourt' | 'Cani-Trail' | 'CanicrossLong' | 'CanicrossCadet' | 'CanicrossMinime' | 'CanicrossBenjamin' | 'Cani-Marche';
  categoryRank?: number;
  averageSpeedKmph?: number | null;
  federation?: string;
  points?: number | string;
  team?: Team; // Optional, populated via join
  event?: Event; // Optional, populated via join
  race?: Race; // Optional, populated via join
  rank?: number; // Virtual property for display (categoryRank ?? position)
  sourceType?: string;
  sourceRecordKey?: string;
  sourceUrl?: string;
  sourceMetadata?: Record<string, unknown> | null;
  statusCode?: ResultStatusCode;
  statusLabel?: string;
  statusRaw?: string;
}

export interface Comment {
  id: string;
  eventId: string;
  authorName: string;
  text: string;
  timestamp: string; // ISO string
}

export interface SearchResults {
  events: Event[];
  teams: Team[];
  humans: Human[];
}

export interface DogBreed {
  id: string;
  name: string;
  category: 'MAIN' | 'SPECIFIC_CROSS' | 'OTHER';
}

export interface Profile {
  id: string; // references auth.users
  username?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  avatarUrl?: string;
  isAdmin: boolean;
  updatedAt?: string;
}
